import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import MyPlayers from "../../components/club/MyPlayers";
import MyStaff from "../../components/club/MyStaff";
import { usePermission } from "../../hooks/usePermission";
import { useSelector } from "react-redux";
import { ROLES } from "../../config/roles";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import MyClubMobileHeader from "../../components/club/responsive/MyClubMobileHeader";
import { FiLogOut } from "react-icons/fi";
import ConfirmationModal from "../../components/club/ConfirmationModal";

const MyClub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("players");
  const [isQuickEdit, setIsQuickEdit] = useState(false);
  const [saveChangesHandler, setSaveChangesHandler] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { currentRole } = useSelector((state) => state.auth);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const playersRef = useRef(null);
  const [isLeaveClubModalOpen, setIsLeaveClubModalOpen] = useState(false);

  const [isLeavingClub, setIsLeavingClub] = useState(false);

  // Permission checks
  const canInviteStaff = usePermission("invite_new_staff");
  const canEditSquad = usePermission("edit_squad");

  // Staff/Coach can see Staff tab if they have invite_new_staff
  const showStaffTab = 
    currentRole !== ROLES.Staff && currentRole !== ROLES.Coach 
      ? true 
      : canInviteStaff;

  // For Staff/Coach without invite permission, force players-only view
  useEffect(() => {
    if (
      (currentRole === ROLES.Staff || currentRole === ROLES.Coach) &&
      !canInviteStaff &&
      activeTab !== "players"
    ) {
      setActiveTab("players");
    }
  }, [currentRole, canInviteStaff, activeTab]);

  const handleSave = async () => {
    if (saveChangesHandler) {
      await saveChangesHandler();
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <div className=" mx-auto pb-6">
        {isMobile ? (
          <MyClubMobileHeader
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isQuickEdit={isQuickEdit}
            setIsQuickEdit={setIsQuickEdit}
            onSave={() => playersRef?.current?.handleSaveChanges()}
            isSaving={isSaving}
            canInviteStaff={canInviteStaff}
            isClubAdmin={currentRole === ROLES.ClubAdmin}
          />
        ) : (
          <div className="px-12 py-4 border-b border-[#E5E6E8] bg-white flex items-center justify-between">
            <h1 className="text-xl font-bold text-[#202630]">My Club</h1>

            {/* {currentRole === ROLES.Staff && (
              <button
                onClick={() => setIsLeaveClubModalOpen(true)}
                disabled={isLeavingClub}
                className="px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
              >
                Leave Club <FiLogOut />
              </button>
            )} */}
          </div>
        )}

        {!isMobile && (
          <div className="flex items-center justify-between border-b border-gray-200 px-12 py-4 mb-4 bg-white">
            {/* Tabs on the left */}
            <div className="flex gap-8 items-center justify-between">
              <>
                <button
                  className={`p-2 rounded-lg text-[16px] ${
                    activeTab === "players"
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC]"
                  }`}
                  onClick={() => setActiveTab("players")}
                >
                  My Players
                </button>
                {showStaffTab && (
                  <button
                    className={`p-2 rounded-lg text-[16px] ${
                      activeTab === "staff"
                        ? "bg-[#F3F4F6] text-[#0B0B0B]"
                        : "text-[#1E1E1ECC]"
                    }`}
                    onClick={() => setActiveTab("staff")}
                  >
                    My Staff
                  </button>
                )}
              </>
            </div>

            {/* Action Buttons on the right */}
            <div className="flex items-center gap-4">
              {activeTab === "players" && canEditSquad && (
                <>
                  <button
                    onClick={() => setIsQuickEdit(!isQuickEdit)}
                    className="flex border-[#E6E6E6] items-center gap-2 px-4 py-2 border rounded-lg text-[#1E1E1E] hover:bg-gray-50"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.33325 2.66714H2.66659C2.31296 2.66714 1.97382 2.80762 1.72378 3.05767C1.47373 3.30772 1.33325 3.64685 1.33325 4.00048V13.3338C1.33325 13.6874 1.47373 14.0266 1.72378 14.2766C1.97382 14.5267 2.31296 14.6671 2.66659 14.6671H11.9999C12.3535 14.6671 12.6927 14.5267 12.9427 14.2766C13.1928 14.0266 13.3333 13.6874 13.3333 13.3338V8.66714M12.3333 1.66714C12.5985 1.40193 12.9582 1.25293 13.3333 1.25293C13.7083 1.25293 14.068 1.40193 14.3333 1.66714C14.5985 1.93236 14.7475 2.29207 14.7475 2.66714C14.7475 3.04222 14.5985 3.40193 14.3333 3.66714L7.99992 10.0005L5.33325 10.6671L5.99992 8.00048L12.3333 1.66714Z"
                        stroke="#2C2C2C"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    Quick edit
                  </button>
                  <Link
                    to="/club/player-availability"
                    className="flex items-center gap-2 px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 1.99955C11.5083 1.82445 11.7162 1.68556 11.945 1.5908C12.1738 1.49604 12.419 1.44727 12.6666 1.44727C12.9142 1.44727 13.1594 1.49604 13.3882 1.5908C13.617 1.68556 13.8248 1.82445 13.9999 1.99955C14.175 2.17465 14.3139 2.38252 14.4087 2.61129C14.5034 2.84006 14.5522 3.08526 14.5522 3.33288C14.5522 3.58051 14.5034 3.8257 14.4087 4.05448C14.3139 4.28325 14.175 4.49112 13.9999 4.66622L4.99992 13.6662L1.33325 14.6662L2.33325 10.9996L11.3333 1.99955Z"
                        stroke="#F5F5F5"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    Change Player Availability
                  </Link>
                </>
              )}
              {activeTab === "staff" && (
                <>
                  {/* Pending/sent staff invites had no entry point anywhere:
                      the My Staff list shows accepted members only, and
                      /invitations was an orphan route nothing linked to. This
                      mirrors the agency side, where pending invites live on
                      the surface you invite from (PendingInvitationsPanel,
                      mounted in pages/agency/AgencyProfile.js). Gated on
                      ClubAdmin to match the /invitations route guard
                      (routes/index.js), so it is never a dead link for a
                      Staff/Coach who only holds invite_new_staff. */}
                  {currentRole === ROLES.ClubAdmin && (
                    <Link
                      to="/invitations"
                      className="flex border-[#E6E6E6] items-center gap-2 px-4 py-2 border rounded-lg text-[#1E1E1E] hover:bg-gray-50"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.6}
                          d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      Sent invitations
                    </Link>
                  )}
                  {canInviteStaff && (
                    <button
                      onClick={() => navigate("/club/invite-staff")}
                      className="flex items-center gap-2 px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Invite Staff
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === "players" || !showStaffTab ? (
          <MyPlayers
            isQuickEdit={isQuickEdit}
            setIsQuickEdit={setIsQuickEdit}
            onRegisterSave={setSaveChangesHandler}
            onSavingStateChange={setIsSaving}
            canEditSquad={canEditSquad}
            ref={playersRef}
          />
        ) : (
          <MyStaff />
        )}
      </div>

      <ConfirmationModal
        isOpen={isLeaveClubModalOpen}
        onClose={() => {
          setIsLeaveClubModalOpen(false);
        }}
        variant="v2"
        message={"Are you sure you want to leave this club?"}
        onConfirm={() => {
          console.log("Leave CLub okay");
          setIsLeaveClubModalOpen(false);
        }}
        confirmText={"Leave Club"}
        title={"Are you sure you want to leave this club?"}
        confirmButtonClass={"bg-[#2283FF] text-white"}
      />
    </div>
  );
};

export default MyClub;
